<template>
	<slideover
		title="Edit Note"
		:open="editing !== null"
		:actions="[
			{
				text: 'Resolve',
				method: resolveNote,
				disabled: !active || !valid || !resolveAllowed,
				icon: 'square-check',
				theme: 'info'
			},
			{
				text: 'Save',
				disabled: !valid,
				method: saveNote,
				icon: 'save'
			}
		]"
		@close="close">
		<app-input v-if="editing" v-model="editing!.notes" name="notes" type="textarea" />
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch, computed } from 'vue'
	import { CollectionItem } from '@/data/schema/collection-item'
	import CollectionService from '@/services/collection'
	import useToaster from '@/stores/toaster'

	const api = new CollectionService()
	const toaster = useToaster()
	const editing = ref<CollectionItem | null>(null)

	const props = withDefaults(
		defineProps<{
			item?: CollectionItem | null
			resolveAllowed?: boolean
			resolveOnSave?: boolean
		}>(),
		{
			item: null,
			resolveAllowed: false,
			resolveOnSave: undefined
		}
	)

	const valid = computed(() => {
		return editing.value?.notes
	})

	const active = computed(() => {
		if (!editing.value) {
			return false
		}
		return (editing.value.notes_status === 2 || editing.value.notes_status === 4) ?? false
	})

	const resolveNote = () => {
		toaster.working({
			text: 'Resolving note...',
			timeout: 0,
			dismissable: false
		})

		const item = {
			id: editing.value!.id,
			notes_status: 3
		}

		api.updateItems([item])
			.then(() => {
				toaster.success({ text: 'Note marked as resolved' })
				editing.value = null
				emit('updated')
				emit('closed')
			})
			.catch((error: any) => {
				toaster.error({ text: 'Error occurred updating note' })
				console.error(error)
			})
			.then(() => {
				toaster.clear()
			})
	}

	const saveNote = () => {
		toaster.working({
			text: 'Saving note...',
			timeout: 0,
			dismissable: false
		})

		const item = {
			id: editing.value!.id,
			notes: editing.value!.notes
		}

		api.updateItems([item], {
			autoResolve: props.resolveOnSave
		})
			.then(() => {
				toaster.clear()
				toaster.success({
					text: 'Note saved successfully'
				})
				emit('updated')
				close()
			})
			.catch((error) => {
				console.log(error)
				toaster.clear()
				toaster.error({ text: 'Error occurred updating note' })
			})
	}

	function close() {
		editing.value = null
		emit('closed')
	}

	watch(
		() => props.item,
		(newVal) => {
			editing.value = newVal ?? null
		}
	)

	// Events
	const emit = defineEmits(['updated', 'closed'])
</script>
